export type AdminSession = {
  access_token: string
  refresh_token?: string
  expires_in?: number
  expires_at?: number
  token_type?: string
  admin: {
    id: string
    email: string
    name: string
    role: string
  }
}

export type DashboardSummary = {
  products: number
  published_products: number
  orders: number
  customers: number
  returns: number
  revenue_aed: number
  latest_orders: AdminOrder[]
}

export type AdminProduct = {
  id: string
  woo_id?: number
  title: string
  handle: string
  status: "draft" | "published" | "archived"
  sku?: string | null
  description?: string | null
  short_description?: string | null
  thumbnail?: string | null
  images: string[]
  categories?: { name: string; handle: string }[]
  price_aed: number
  inventory_quantity: number
  variants: AdminVariant[]
  seo_title?: string | null
  seo_description?: string | null
  google_product_category?: string | null
  brand?: string | null
  metadata?: Record<string, unknown>
  updated_at?: string
}

export type AdminVariant = {
  id: string
  product_id: string
  title: string
  sku?: string | null
  price_aed: number
  compare_at_price_aed?: number | null
  inventory_quantity: number
  manage_inventory?: boolean
  allow_backorder?: boolean
  thumbnail?: string | null
}

export type AdminOrder = {
  id: string
  display_id?: number
  status: string
  payment_status: string
  fulfillment_status: string
  email?: string
  total: number
  subtotal: number
  shipping_total: number
  discount_total?: number
  payment_method?: string
  created_at?: string
  shipping_address?: Record<string, string>
  billing_address?: Record<string, string>
  customer_note?: string
  admin_note?: string
  tracking_number?: string
  items: Array<{
    id: string
    title: string
    quantity: number
    unit_price: number
    total: number
    thumbnail?: string | null
    metadata?: Record<string, unknown> | null
  }>
}

export type AdminCustomer = {
  id: string
  email: string
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  created_at?: string
  billing_address?: Record<string, string>
}

export type ReturnRecord = {
  id: string
  order_id?: string
  status: string
  reason?: string
  customer_email?: string
  amount_aed?: number
  created_at?: string
}

export type AdminSettings = {
  payments: {
    cod_enabled: boolean
    stripe_enabled: boolean
    stripe_mode: "test" | "live"
    stripe_test_configured: boolean
    stripe_live_configured: boolean
    stripe_secret_configured: boolean
    stripe_publishable_configured: boolean
    stripe_publishable_key?: string
    providers: string[]
  }
  email: {
    order_notifications_enabled: boolean
    admin_order_emails_enabled: boolean
    admin_order_email?: string
    customer_order_emails_enabled: boolean
    from_configured: boolean
    from_address?: string
    admin_recipient_configured: boolean
    resend_configured?: boolean
  }
  shipping: {
    free_shipping_threshold_aed: number
    delivery_fee_aed: number
  }
}

export type AdminUser = {
  id: string
  email: string
  name: string
  role: string
  enabled: boolean
  created_at?: string
}

export type CreateAdminUserInput = Partial<AdminUser> & {
  password?: string
}

export type AdminCoupon = {
  id: string
  code: string
  type: "fixed" | "percentage"
  amount_aed: number
  percentage?: number | null
  minimum_subtotal_aed?: number | null
  usage_limit?: number | null
  used_count: number
  enabled: boolean
  starts_at?: string | null
  ends_at?: string | null
  created_at?: string
}

const API_URL =
  import.meta.env.VITE_EDGE_API_URL?.replace(/\/$/, "") ||
  "http://localhost:8787"

const TOKEN_KEY = "dentix_admin_access_token"
const SESSION_KEY = "dentix_admin_session"
export const ADMIN_SESSION_EXPIRED_EVENT = "dentix_admin_session_expired"

class AdminApiError extends Error {
  status: number
  code: string

  constructor(message: string, status: number, code = "api_error") {
    super(message)
    this.name = "AdminApiError"
    this.status = status
    this.code = code
  }
}

export function getStoredSession(): AdminSession | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AdminSession
  } catch {
    return null
  }
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(SESSION_KEY)
}

function storeSession(session: AdminSession) {
  const current = getStoredSession()
  const next: AdminSession = {
    ...session,
    refresh_token: session.refresh_token || current?.refresh_token,
    expires_at:
      session.expires_at ||
      (session.expires_in
        ? Math.floor(Date.now() / 1000) + Number(session.expires_in)
        : current?.expires_at),
  }

  localStorage.setItem(TOKEN_KEY, next.access_token)
  localStorage.setItem(SESSION_KEY, JSON.stringify(next))
  return next
}

function expireSession() {
  clearSession()
  window.dispatchEvent(new Event(ADMIN_SESSION_EXPIRED_EVENT))
}

export async function login(email: string, password: string) {
  const session = await rawRequest<AdminSession>("/admin/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  })
  return storeSession(session)
}

export async function getMe() {
  return request<{ admin: AdminSession["admin"] }>("/admin/auth/me")
}

export async function getSummary() {
  return request<DashboardSummary>("/admin/summary")
}

export async function getSettings() {
  return request<{ settings: AdminSettings }>("/admin/settings")
}

export async function updateSettings(body: Partial<AdminSettings>) {
  return request<{ settings: AdminSettings }>("/admin/settings", {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export async function getAdminUsers() {
  return request<{ admin_users: AdminUser[]; count: number }>("/admin/admin-users")
}

export async function createAdminUser(body: CreateAdminUserInput) {
  return request<{ admin_user: AdminUser }>("/admin/admin-users", {
    method: "POST",
    body: JSON.stringify(body),
  })
}

export async function updateAdminUser(id: string, body: Partial<AdminUser>) {
  return request<{ admin_user: AdminUser }>(`/admin/admin-users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export async function deleteAdminUser(id: string) {
  return request<{ deleted: boolean; id: string }>(`/admin/admin-users/${id}`, {
    method: "DELETE",
  })
}

export async function getCoupons() {
  return request<{ coupons: AdminCoupon[]; count: number }>("/admin/coupons")
}

export async function createCoupon(body: Partial<AdminCoupon>) {
  return request<{ coupon: AdminCoupon }>("/admin/coupons", {
    method: "POST",
    body: JSON.stringify(body),
  })
}

export async function updateCoupon(id: string, body: Partial<AdminCoupon>) {
  return request<{ coupon: AdminCoupon }>(`/admin/coupons/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export async function getProducts() {
  return request<{ products: AdminProduct[]; count: number }>("/admin/products")
}

export async function updateProduct(
  id: string,
  body: Partial<AdminProduct> & { variant?: Partial<AdminVariant> }
) {
  return request<{ product: AdminProduct }>(`/admin/products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export async function uploadProductImage(file: File) {
  const form = new FormData()
  form.set("file", file)
  return request<{ key: string; url: string }>("/admin/uploads", {
    method: "POST",
    body: form,
    skipContentType: true,
  })
}

export async function getOrders() {
  return request<{ orders: AdminOrder[]; count: number }>("/admin/orders")
}

export async function updateOrder(id: string, body: Partial<AdminOrder>) {
  return request<{ order: AdminOrder }>(`/admin/orders/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export async function getCustomers() {
  return request<{ customers: AdminCustomer[]; count: number }>("/admin/customers")
}

export async function getReturns() {
  return request<{ returns: ReturnRecord[]; refunds: ReturnRecord[]; count: number }>(
    "/admin/returns"
  )
}

export async function updateReturn(id: string, body: Partial<ReturnRecord>) {
  return request<{ return: ReturnRecord }>(`/admin/returns/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

let refreshPromise: Promise<AdminSession | null> | null = null

async function request<T>(
  path: string,
  init: RequestInit & { skipContentType?: boolean } = {}
): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY)
  try {
    return await rawRequest<T>(path, init, token)
  } catch (error) {
    if (shouldRefreshSession(error, path, token)) {
      const refreshed = await refreshSession()
      if (refreshed) {
        return rawRequest<T>(path, init, refreshed.access_token)
      }
      throw new AdminApiError(
        "Your admin session expired. Please sign in again.",
        401,
        "session_expired"
      )
    }
    throw error
  }
}

async function refreshSession() {
  const session = getStoredSession()
  if (!session?.refresh_token) {
    expireSession()
    return null
  }

  if (!refreshPromise) {
    refreshPromise = rawRequest<AdminSession>("/admin/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    })
      .then(storeSession)
      .catch(() => {
        expireSession()
        return null
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

function shouldRefreshSession(error: unknown, path: string, token: string | null) {
  if (!token || path === "/admin/auth/login" || path === "/admin/auth/refresh") {
    return false
  }
  if (!(error instanceof AdminApiError)) return false
  if (error.status !== 401 && error.status !== 403) return false
  return (
    error.code === "session_expired" ||
    error.code === "bad_jwt" ||
    error.message.toLowerCase().includes("expired") ||
    error.message.toLowerCase().includes("invalid jwt")
  )
}

async function rawRequest<T>(
  path: string,
  init: RequestInit & { skipContentType?: boolean } = {},
  token?: string | null
): Promise<T> {
  const { skipContentType, ...requestInit } = init
  const headers = new Headers(init.headers)
  if (!skipContentType && init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json")
  }
  if (token) headers.set("authorization", `Bearer ${token}`)

  const response = await fetch(`${API_URL}${path}`, {
    ...requestInit,
    headers,
  })

  if (!response.ok) {
    const error = await readApiError(response)
    throw new AdminApiError(error.message, response.status, error.code)
  }

  if (response.status === 204) return null as T
  return response.json() as Promise<T>
}

async function readApiError(response: Response) {
  const text = await response.text()
  let code = "api_error"
  let message = text || response.statusText

  try {
    const payload = JSON.parse(text) as Record<string, unknown>
    code =
      stringValue(payload.code) ||
      stringValue(payload.error_code) ||
      stringValue(payload.error) ||
      code
    message =
      stringValue(payload.message) ||
      stringValue(payload.msg) ||
      stringValue(payload.error_description) ||
      message
  } catch {
    // Keep the original response body when the API returns plain text.
  }

  const normalized = `${code} ${message}`.toLowerCase()
  if (
    code === "session_expired" ||
    code === "bad_jwt" ||
    normalized.includes("invalid jwt") ||
    normalized.includes("token is expired")
  ) {
    return {
      code: "session_expired",
      message: "Your admin session expired. Please sign in again.",
    }
  }

  if (code === "invalid_credentials" || normalized.includes("invalid login credentials")) {
    return {
      code: "invalid_credentials",
      message: "Email or password is incorrect.",
    }
  }

  return { code, message }
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value : ""
}
